import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Grid2, useMediaQuery, useTheme } from "@mui/material";
import { useNewsletterEditor } from "./context/NewsletterEditorContext";
import EditorToolbar from "./panels/EditorToolbar";
import LeftPanel from "./panels/LeftPanel";
import CanvasPanel from "./panels/CanvasPanel";
import PropertiesPanel from "./panels/PropertiesPanel";
import EditorWorkspaceOverlay from "./panels/EditorWorkspaceOverlay";
import EditorMobileOverlay from "./panels/EditorMobileOverlay";
import { createEmptyDocument } from "./model/documentModel";
import useEditorViewportHeight from "./hooks/useEditorViewportHeight";
import { contentSectionPaperSx } from "../content/contentEditorStyles";

const panelShellSx = {
  height: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: 1,
};

const EditorWorkspaceBody = ({ onCompile, openListSignal, onCreateNew }) => {
  const { workspaceActive, activateWorkspace, resetDocument } = useNewsletterEditor();

  const handleCreateNew = () => {
    resetDocument(createEmptyDocument());
    activateWorkspace();
    onCreateNew?.();
  };

  return (
    <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
      <Grid2
        container
        sx={{
          height: "100%",
          filter: workspaceActive ? "none" : "blur(4px)",
          pointerEvents: workspaceActive ? "auto" : "none",
          userSelect: workspaceActive ? "auto" : "none",
        }}
      >
        <Grid2 size={{ xs: 12, md: 3 }} sx={panelShellSx}>
          <LeftPanel />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }} sx={panelShellSx}>
          <CanvasPanel onCompile={onCompile} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 3 }} sx={panelShellSx}>
          <PropertiesPanel />
        </Grid2>
      </Grid2>
      {!workspaceActive ? (
        <EditorWorkspaceOverlay
          onCreateNew={handleCreateNew}
          onOpenExisting={() => openListSignal?.()}
        />
      ) : null}
    </Box>
  );
};

EditorWorkspaceBody.propTypes = {
  onCompile: PropTypes.func.isRequired,
  openListSignal: PropTypes.func,
  onCreateNew: PropTypes.func,
};

const NewsletterEditorInner = () => {
  const rootRef = useRef(null);
  const panelHeight = useEditorViewportHeight(rootRef);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { workspaceActive } = useNewsletterEditor();
  const [compileErrors, setCompileErrors] = useState([]);
  const [openListSignal, setOpenListSignal] = useState(0);
  const handleCompile = useCallback((errors) => setCompileErrors(errors || []), []);
  const requestOpenList = useCallback(() => setOpenListSignal((value) => value + 1), []);
  const blockMobileEditor = workspaceActive && !isDesktop;

  return (
    <Box
      ref={rootRef}
      sx={{
        ...contentSectionPaperSx,
        height: panelHeight,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          filter: blockMobileEditor ? "blur(4px)" : "none",
          pointerEvents: blockMobileEditor ? "none" : "auto",
          userSelect: blockMobileEditor ? "none" : "auto",
        }}
      >
        <EditorToolbar compileErrors={compileErrors} openListSignal={openListSignal} />
        <EditorWorkspaceBody onCompile={handleCompile} openListSignal={requestOpenList} />
      </Box>
      {blockMobileEditor ? <EditorMobileOverlay /> : null}
    </Box>
  );
};

export default NewsletterEditorInner;
 